// Key changes: Remove cleanup at start of useEffect, add guard flags to prevent duplicate listeners

import { useState, useEffect, useRef } from 'react';
import {
  signInAnonymously,
  onAuthStateChanged,
  signInWithCustomToken,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification,
  signOut as firebaseSignOut,
  User as FirebaseUser,
} from 'firebase/auth';
import {
  collection,
  deleteDoc,
  doc,
  setDoc,
  onSnapshot,
  query,
  where,
  getDocs,
  getDoc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { auth, db, appId, googleProvider } from '@/lib/firebase';
import {
  isRateLimited,
  recordSignInAttempt,
  getRateLimitResetTime,
  formatRateLimitTime,
  cacheSession,
  getCachedSession,
  clearSessionCache,
  cachePhoto,
  getCachedPhoto,
  clearPhotoCache,
  updateCachedPhoto,
} from '@/lib/auth-cache';
import { fetchAndCachePhotoDataUrl } from '@/lib/photo-blob-cache';
import { completeDonorClaimIfPresent } from '@/lib/donor-claim';
import { Routes, Route, useNavigate, useLocation, useParams, Navigate } from 'react-router-dom';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { PageShell, PageMain } from './components/PageShell';
import { ProfileModal } from './components/ProfileModal';
import { ScrollToTop } from './components/ScrollToTop';
import { Toast } from './components/ui/Toast';
import { ConfirmationModal } from './components/ui/ConfirmationModal';
import { AdminPanel } from './components/AdminPanel';
import type { AdminView } from './components/AdminSidebar';
import { TermsOfService } from './components/LegalPages/TermsOfService';
import { PrivacyPolicy } from './components/LegalPages/PrivacyPolicy';
import { CustomerDashboardPage } from './pages/CustomerDashboardPage';
import { CustomerProfilePage } from './pages/CustomerProfilePage';
import { FederalReadinessResultsPage } from './pages/FederalReadinessResultsPage';
import { CustomerBillingPage } from './pages/CustomerBillingPage';
import { CustomerServicesPage } from './pages/CustomerServicesPage';
import { DonorDashboardPage } from './pages/DonorDashboardPage';
import { CMSPageView } from './pages/CMSPageView';
import { CMSPageViewRoute } from './pages/CMSPageViewRoute';
import { NotFoundPage } from './pages/NotFoundPage';
import { ArticleDetailPage } from './pages/ArticleDetailPage';
import { AllArticlesPage } from './pages/AllArticlesPage';
import { BookStrategyCallPage } from './pages/BookStrategyCallPage';
import { DonatePage } from './pages/DonatePage';
import { DonateThankYouPage } from './pages/DonateThankYouPage';
import { WarriorsPage } from './pages/WarriorsPage';
import { CorporatePartnersPage } from './pages/CorporatePartnersPage';
import { OfflineNotice } from './components/OfflineNotice';
import { UrgentBanner, type UrgentBannerSettings } from './components/UrgentBanner';
import { AnalyticsScripts } from './components/AnalyticsScripts';
import { CookieConsentBanner } from './components/CookieConsentBanner';
import { useOnlineStatus } from './hooks/useOnlineStatus';
import { CookieConsentProvider } from '@/context/CookieConsentContext';

import {
  getSEOOverrideByPath,
  getAllNavGroups,
  getAllNavItems,
  getHeaderConfig,
  getFooterConfig,
  getHeaderHtml,
  getFooterHtml,
  type NavGroup,
  type NavItem,
  type HeaderConfig,
  type FooterConfig,
  type LinkPreviewSettings,
} from '@/lib/cms';
import { getPlaceholderReplacements, replaceCmsPlaceholders } from '@/lib/cms-placeholders';
import { setGSGOMetaTags, setPageTitle, setMetaDescription, setOpenGraphTags, setTwitterCardTags, setPageMeta, setAuthorMeta, setArticlePublishedTime } from '@/utils/metaTags';

interface UserProfile {
  displayName: string;
  photoUrl?: string | null;
}

interface Notification {
  message: string;
  type: 'success' | 'error' | 'info';
}

interface ConfirmationState {
  isOpen: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
}

interface AdminData {
  role: 'root' | 'admin' | 'moderator';
  allowedModules?: AdminView[] | null;
  email: string;
  displayName: string;
  photoUrl: string | null;
  isActive: boolean;
  createdAt: Timestamp | Date;
  createdBy: string | null;
  updatedAt: Timestamp | Date;
  updatedBy: string | null;
}

interface BrandingSettings {
  brandName?: string;
  brandTagline?: string;
  logoUrl?: string;
  metaDescription?: string;
}

/** Contact info from App Settings (phone, email, company address, strategy-call notification). */
export interface ContactSettings {
  contactPhone?: string;
  contactEmail?: string;
  companyAddress?: string;
  /** Email to receive Strategy Call form notifications (Brevo). */
  notificationEmail?: string;
  /** Email(s) to receive Website Service Request notifications (Brevo). */
  websiteRequestNotificationEmail?: string;
  /** Email(s) to receive ambassador application notifications (CF-6, Brevo). */
  ambassadorNotificationEmail?: string;
  /** Sender name/email for Brevo Strategy Call emails. */
  brevoSenderName?: string;
  brevoSenderEmail?: string;
}

/** Path segments that are not CMS pages (reserved for app routes). */
const CMS_RESERVED_SLUGS = ['admin', 'dashboard', 'privacy', 'terms', 'about', 'lp', 'warriors'];

/** Renders a CMS page by slug at /:slug, or NotFoundPage if slug is reserved or page not found. */
function CMSBySlugOrNotFound({
  brandName,
  branding,
  contact,
  onOpenStrategyCall,
}: {
  brandName?: string | null;
  branding?: BrandingSettings | null;
  contact?: ContactSettings | null;
  onOpenStrategyCall?: () => void;
}) {
  const { slug } = useParams<{ slug: string }>();
  if (!slug || CMS_RESERVED_SLUGS.includes(slug.toLowerCase())) {
    return <NotFoundPage />;
  }
  return (
    <CMSPageView
      path={`/${slug}`}
      slug={slug}
      brandName={brandName}
      branding={branding}
      contact={contact}
      onOpenStrategyCall={onOpenStrategyCall}
    />
  );
}

function CMSByNestedPath({
  segment,
  brandName,
  branding,
  contact,
  onOpenStrategyCall,
}: {
  segment: string;
  brandName?: string | null;
  branding?: BrandingSettings | null;
  contact?: ContactSettings | null;
  onOpenStrategyCall?: () => void;
}): JSX.Element {
  const { slug } = useParams<{ slug: string }>();
  if (!slug) return <NotFoundPage />;
  return (
    <CMSPageView
      path={`/${segment}/${slug}`}
      slug={`${segment}/${slug}`}
      brandName={brandName}
      branding={branding}
      contact={contact}
      onOpenStrategyCall={onOpenStrategyCall}
    />
  );
}

function App() {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminRole, setAdminRole] = useState<'root' | 'admin' | 'moderator' | null>(null);
  const [adminAllowedModules, setAdminAllowedModules] = useState<AdminView[] | null>(null);
  const isOnline = useOnlineStatus();
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [notification, setNotification] = useState<Notification | null>(null);
  const [confirmation, setConfirmation] = useState<ConfirmationState>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });
  const [allowAnonymousSignIn, setAllowAnonymousSignIn] = useState<boolean | null>(null); // null = not loaded yet
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const previousSettingRef = useRef<boolean | null>(null);
  const [branding, setBranding] = useState<BrandingSettings | null>(null);
  const [linkPreview, setLinkPreview] = useState<LinkPreviewSettings | null>(null);
  const [contact, setContact] = useState<ContactSettings | null>(null);
  const [navGroups, setNavGroups] = useState<NavGroup[]>([]);
  const [navItems, setNavItems] = useState<NavItem[]>([]);
  const [headerConfig, setHeaderConfig] = useState<HeaderConfig | null>(null);
  const [footerConfig, setFooterConfig] = useState<FooterConfig | null>(null);
  const [headerHtml, setHeaderHtml] = useState<string | null>(null);
  const [footerHtml, setFooterHtml] = useState<string | null>(null);
  const [urgentBanner, setUrgentBanner] = useState<UrgentBannerSettings | null>(null);
  const location = useLocation();
  /** True after first Firebase auth state has been received (avoids redirecting before session restore on refresh). */
  const [authReady, setAuthReady] = useState(false);
  /** True when admin role check for current user has completed (so we don't redirect /admin before Firestore resolves). */
  const [adminCheckComplete, setAdminCheckComplete] = useState(true);

  // Fetch admin settings for anonymous sign-in
  useEffect(() => {
    const settingsRef = doc(db, 'artifacts', appId, 'settings', 'auth');
    const unsubscribe = onSnapshot(
      settingsRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const settings = docSnap.data();
          const allowAnonymous = settings.allowAnonymousSignIn ?? true;
          const previousValue = previousSettingRef.current;
          previousSettingRef.current = allowAnonymous;
          setAllowAnonymousSignIn(allowAnonymous);
          setSettingsLoaded(true);
          console.log('[Auth] Admin settings loaded:', { 
            allowAnonymousSignIn: allowAnonymous, 
            previousValue,
            settings 
          });
          
          // If setting changed from enabled to disabled, sign out anonymous users
          if (previousValue === true && allowAnonymous === false && user?.isAnonymous) {
            console.log('[Auth] Anonymous sign-in was disabled, signing out anonymous user');
            firebaseSignOut(auth).catch((error: Error) => {
              console.error('[Auth] Sign-out failed:', error);
            });
          }
        } else {
          // Default to enabled if settings don't exist
          const defaultValue = true;
          previousSettingRef.current = defaultValue;
          setAllowAnonymousSignIn(defaultValue);
          setSettingsLoaded(true);
          console.log('[Auth] No admin settings found, using defaults (enabled)');
        }
      },
      (error) => {
        console.error('[Auth] Error fetching admin settings:', error);
        // Default to enabled on error
        const defaultValue = true;
        previousSettingRef.current = defaultValue;
        setAllowAnonymousSignIn(defaultValue);
        setSettingsLoaded(true);
        console.log('[Auth] Error loading settings, defaulting to enabled');
      }
    );
    return () => unsubscribe();
  }, [user]);

  // Initialize meta tags on mount (before branding loads)
  useEffect(() => {
    const baseUrl = window.location.origin;
    setGSGOMetaTags({
      url: baseUrl,
      image: `${baseUrl}/social-share-gsgo.jpeg`,
    });
  }, []);

  // Branding settings (brand name, tagline, logo, meta description)
  useEffect(() => {
    const brandingRef = doc(db, 'artifacts', appId, 'settings', 'branding');

    const unsubscribe = onSnapshot(
      brandingRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data() as BrandingSettings;
          setBranding(data);
          console.log('[Branding] Loaded branding settings from Firestore:', data);
        } else {
          // Provide sensible defaults if branding doc does not exist
          const defaultBranding: BrandingSettings = {
            brandName: 'GSGO',
            brandTagline: 'Global Support for Growth and Opportunity',
          };
          setBranding(defaultBranding);
          console.log(
            '[Branding] No branding document found, using default branding values:',
            defaultBranding
          );
        }
      },
      (error) => {
        console.error('[Branding] Error fetching branding settings:', error);
        // On error, fall back to hard-coded defaults
        setBranding({
          brandName: 'GSGO',
          brandTagline: 'Global Support for Growth and Opportunity',
        });
      }
    );

    return () => unsubscribe();
  }, []);

  // Link preview settings (default og:image, author, publish date for shared links)
  useEffect(() => {
    const linkPreviewRef = doc(db, 'artifacts', appId, 'settings', 'linkPreview');
    const unsubscribe = onSnapshot(
      linkPreviewRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setLinkPreview({
            ogImage: data.ogImage ?? null,
            author: data.author ?? null,
            publishDate: data.publishDate ?? null,
            updatedAt: data.updatedAt ?? null,
            updatedBy: data.updatedBy ?? null,
          });
        } else {
          setLinkPreview(null);
        }
      },
      () => setLinkPreview(null)
    );
    return () => unsubscribe();
  }, []);

  // Contact settings (phone, email, company address) for header/footer
  useEffect(() => {
    const contactRef = doc(db, 'artifacts', appId, 'settings', 'contact');
    const unsubscribe = onSnapshot(
      contactRef,
      (docSnap) => {
        if (docSnap.exists()) {
          setContact(docSnap.data() as ContactSettings);
        } else {
          setContact({ contactPhone: '+1 832-542-6260' });
        }
      },
      () => {
        setContact({ contactPhone: '+1 832-542-6260' });
      }
    );
    return () => unsubscribe();
  }, []);

  // Urgent banner settings (top public notice bar)
  useEffect(() => {
    const urgentBannerRef = doc(db, 'artifacts', appId, 'settings', 'urgentBanner');
    const unsubscribe = onSnapshot(
      urgentBannerRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setUrgentBanner({
            enabled: data.enabled === true,
            label: typeof data.label === 'string' ? data.label : 'URGENT:',
            message: typeof data.message === 'string' ? data.message : '',
            ctaLabel: typeof data.ctaLabel === 'string' ? data.ctaLabel : 'GIVE NOW →',
            ctaHref: typeof data.ctaHref === 'string' ? data.ctaHref : '/donate#donationwidget',
            backgroundColor:
              typeof data.backgroundColor === 'string' ? data.backgroundColor : '#C0392B',
            updatedAt: data.updatedAt ?? null,
            updatedBy: typeof data.updatedBy === 'string' ? data.updatedBy : '',
          });
        } else {
          setUrgentBanner({
            enabled: false,
            label: 'URGENT:',
            message: '',
            ctaLabel: 'GIVE NOW →',
            ctaHref: '/donate#donationwidget',
            backgroundColor: '#C0392B',
          });
        }
      },
      () => {
        setUrgentBanner({
          enabled: false,
          label: 'URGENT:',
          message: '',
          ctaLabel: 'GIVE NOW →',
          ctaHref: '/donate#donationwidget',
          backgroundColor: '#C0392B',
        });
      }
    );
    return () => unsubscribe();
  }, []);

  // Load CMS navigation data (nav groups, nav items, header/footer config, header/footer HTML)
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [groups, items, hCfg, fCfg, hHtml, fHtml] = await Promise.all([
          getAllNavGroups(),
          getAllNavItems(),
          getHeaderConfig(),
          getFooterConfig(),
          getHeaderHtml(),
          getFooterHtml(),
        ]);
        if (cancelled) return;
        setNavGroups(groups);
        setNavItems(items);
        setHeaderConfig(hCfg);
        setFooterConfig(fCfg);
        setHeaderHtml(hHtml);
        setFooterHtml(fHtml);
      } catch (err) {
        console.error('[App] Failed to load CMS navigation data:', err);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // Keep document title and meta tags in sync with branding and link preview settings
  useEffect(() => {
    const baseUrl = window.location.origin;
    const defaultOgImage = linkPreview?.ogImage?.trim() || `${baseUrl}/social-share-gsgo.jpeg`;

    if (branding) {
      const title = branding.brandName
        ? `${branding.brandName} — ${branding.brandTagline || 'Global Support for Growth and Opportunity'}`
        : 'GSGO — Global Support for Growth and Opportunity';

      const description = branding.metaDescription ||
        'GSGO supports CKD patients and families through donor-powered programs.';

      setPageTitle(title);
      setMetaDescription(description);

      setOpenGraphTags({
        title: branding.brandName
          ? `${branding.brandName} — Global Support for Growth and Opportunity`
          : 'GSGO — Global Support for Growth and Opportunity',
        description,
        type: 'website',
        url: baseUrl,
        image: defaultOgImage,
      });

      setTwitterCardTags({
        card: 'summary_large_image',
        title: branding.brandName ? branding.brandName : 'GSGO',
        description,
        image: defaultOgImage,
      });
    } else {
      setGSGOMetaTags({ url: baseUrl, image: defaultOgImage });
    }

    if (linkPreview?.author?.trim()) {
      setAuthorMeta(linkPreview.author.trim());
    }
    if (linkPreview?.publishDate?.trim()) {
      setArticlePublishedTime(linkPreview.publishDate.trim());
    }
  }, [branding, linkPreview]);

  // SEO: page-specific meta for static routes (About, Privacy, Terms, home); prefer CMS overrides when present
  useEffect(() => {
    const baseUrl = window.location.origin;
    const pathname = location.pathname;
    const staticPaths = ['/', '/about-us', '/privacy-policy', '/terms-of-service'];
    const defaultImage = linkPreview?.ogImage?.trim() || undefined;

    if (!staticPaths.includes(pathname)) return;

    let cancelled = false;
    getSEOOverrideByPath(pathname).then((override) => {
      if (cancelled) return;
      if (override && (override.title ?? override.description ?? override.ogImage)) {
        const url = pathname === '/' ? baseUrl : `${baseUrl}${pathname}`;
        setPageMeta({
          title: override.title ?? undefined,
          description: override.description ?? undefined,
          keywords: override.keywords ?? undefined,
          url,
          image: override.ogImage ?? defaultImage,
          ogTitle: override.ogTitle ?? undefined,
          ogDescription: override.ogDescription ?? undefined,
          twitterCard: override.twitterCard ?? undefined,
          twitterTitle: override.twitterTitle ?? undefined,
          twitterDescription: override.twitterDescription ?? undefined,
          canonical: override.canonical ?? url,
          noIndex: override.noIndex ?? false,
          schemaJson: override.schemaJson ?? undefined,
        });
        return;
      }
      // Fallback: hardcoded defaults per route
      if (pathname === '/about-us') {
        setPageMeta({
          title: 'About | GSGO',
          description: 'Learn about GSGO, our mission, and how we support families facing chronic kidney disease.',
          url: `${baseUrl}/about-us`,
          canonical: `${baseUrl}/about-us`,
          image: defaultImage,
        });
        return;
      }
      if (pathname === '/privacy-policy') {
        setPageMeta({
          title: 'Privacy Policy | GSGO',
          description: 'GSGO privacy policy. How we collect, use, and protect your information.',
          url: `${baseUrl}/privacy-policy`,
          canonical: `${baseUrl}/privacy-policy`,
          image: defaultImage,
        });
        return;
      }
      if (pathname === '/terms-of-service') {
        setPageMeta({
          title: 'Terms of Service | GSGO',
          description: 'GSGO terms of service. Terms and conditions for using our platform.',
          url: `${baseUrl}/terms-of-service`,
          canonical: `${baseUrl}/terms-of-service`,
          image: defaultImage,
        });
        return;
      }
      if (pathname === '/' && branding) {
        const title = branding.brandName
          ? `${branding.brandName} — ${branding.brandTagline || 'Global Support for Growth and Opportunity'}`
          : 'GSGO — Global Support for Growth and Opportunity';
        const description = branding.metaDescription || 'GSGO supports CKD patients and families through donor-powered programs.';
        setPageMeta({
          title,
          description,
          url: baseUrl,
          canonical: `${baseUrl}/`,
          image: defaultImage,
        });
      }
    }).catch(() => {
      if (!cancelled) {
        if (pathname === '/about-us') {
          setPageMeta({
            title: 'About | GSGO',
            description: 'Learn about GSGO, our mission, and how we support families facing chronic kidney disease.',
            url: `${baseUrl}/about-us`,
            canonical: `${baseUrl}/about-us`,
            image: defaultImage,
          });
        } else if (pathname === '/privacy-policy') {
          setPageMeta({
            title: 'Privacy Policy | GSGO',
            description: 'GSGO privacy policy. How we collect, use, and protect your information.',
            url: `${baseUrl}/privacy-policy`,
            canonical: `${baseUrl}/privacy-policy`,
            image: defaultImage,
          });
        } else if (pathname === '/terms-of-service') {
          setPageMeta({
            title: 'Terms of Service | GSGO',
            description: 'GSGO terms of service. Terms and conditions for using our platform.',
            url: `${baseUrl}/terms-of-service`,
            canonical: `${baseUrl}/terms-of-service`,
            image: defaultImage,
          });
        } else if (pathname === '/' && branding) {
          const title = branding.brandName
            ? `${branding.brandName} — ${branding.brandTagline || 'Global Support for Growth and Opportunity'}`
            : 'GSGO — Global Support for Growth and Opportunity';
          const description = branding.metaDescription || 'GSGO supports CKD patients and families through donor-powered programs.';
          setPageMeta({ title, description, url: baseUrl, canonical: `${baseUrl}/`, image: defaultImage });
        }
      }
    });
    return () => { cancelled = true; };
  }, [location.pathname, branding, linkPreview]);

  // Auth Init with caching
  const authReadyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastAdminCheckUserIdRef = useRef<string | null>(null);
  useEffect(() => {
    let hasInitialized = false;
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('[Auth] Auth state changed:', user ? `${user.uid} (anonymous: ${user.isAnonymous})` : 'null');
      setUser(user);

      if (user) {
        if (authReadyTimeoutRef.current) {
          clearTimeout(authReadyTimeoutRef.current);
          authReadyTimeoutRef.current = null;
        }
        setAuthReady(true);
        // Only reset admin check when user identity changes; repeated auth callbacks (e.g. token refresh) must not clear it
        if (lastAdminCheckUserIdRef.current !== user.uid) {
          lastAdminCheckUserIdRef.current = user.uid;
          setAdminCheckComplete(false);
        }
      } else {
        lastAdminCheckUserIdRef.current = null;
        // Defer authReady when user is null so Firebase persistence can restore session first
        // (avoids redirecting /admin to / on refresh before restored user is set)
        if (authReadyTimeoutRef.current) clearTimeout(authReadyTimeoutRef.current);
        authReadyTimeoutRef.current = setTimeout(() => {
          authReadyTimeoutRef.current = null;
          setAuthReady(true);
        }, 400);
      }

      if (!user) {
        setUserProfile(null);
        setIsAdmin(false);
        setAdminRole(null);
        setAdminAllowedModules(null);
        
        // Only attempt anonymous sign-in if:
        // 1. Settings have been loaded
        // 2. Anonymous sign-in is enabled
        // 3. We've already initialized (to avoid immediate sign-in on mount)
        // 4. No custom auth token is being used
        if (
          hasInitialized && 
          !import.meta.env.VITE_INITIAL_AUTH_TOKEN && 
          settingsLoaded && 
          allowAnonymousSignIn === true
        ) {
          console.log('[Auth] No user after persistence check, signing in anonymously (settings allow it)');
          await signInAnonymously(auth).catch((error: Error) => {
            console.error('[Auth] Anonymous sign-in failed:', error);
          });
        } else if (hasInitialized && settingsLoaded && allowAnonymousSignIn === false) {
          console.log('[Auth] Anonymous sign-in is disabled, user must sign in with Google or Email');
        } else if (!settingsLoaded) {
          console.log('[Auth] Waiting for settings to load before attempting anonymous sign-in');
        }
      } else {
        // Cache successful session
        cacheSession(user.uid);
        
        // If anonymous sign-in is disabled and user is anonymous, sign them out
        if (settingsLoaded && allowAnonymousSignIn === false && user.isAnonymous) {
          console.log('[Auth] Anonymous sign-in disabled, signing out anonymous user');
          await firebaseSignOut(auth).catch((error: Error) => {
            console.error('[Auth] Sign-out failed:', error);
          });
        }
      }
      
      hasInitialized = true;
      if (settingsLoaded) {
        // Auth and settings ready
      }
    });
    
    const initialAuthToken = import.meta.env.VITE_INITIAL_AUTH_TOKEN;
    if (initialAuthToken) {
      console.log('[Auth] Using custom auth token');
      signInWithCustomToken(auth, initialAuthToken).catch((error: Error) => {
        console.error('[Auth] Custom token sign-in failed:', error);
      });
    }
    
    return () => {
      if (authReadyTimeoutRef.current) {
        clearTimeout(authReadyTimeoutRef.current);
        authReadyTimeoutRef.current = null;
      }
      unsubscribe();
    };
  }, [allowAnonymousSignIn, settingsLoaded]);

  // Admin Role Fetching - robust validation with fallback to direct fetch
  const adminUnsubscribeRef = useRef<(() => void) | null>(null);
  const adminCheckAttemptRef = useRef(0);
  
  useEffect(() => {
    if (!user) {
      setIsAdmin(false);
      setAdminRole(null);
      setAdminAllowedModules(null);
      setAdminCheckComplete(true);
      // Clean up existing listener
      if (adminUnsubscribeRef.current) {
        adminUnsubscribeRef.current();
        adminUnsubscribeRef.current = null;
      }
      adminCheckAttemptRef.current = 0;
      return;
    }

    setAdminCheckComplete(false);

    // If listener already exists, don't create a new one (state already set from previous run)
    if (adminUnsubscribeRef.current) {
      console.log('[Admin] Listener already exists, skipping duplicate setup');
      setAdminCheckComplete(true);
      return;
    }

    adminCheckAttemptRef.current += 1;
    const attemptNumber = adminCheckAttemptRef.current;

    // Safety: if Firestore check hangs, stop showing loading after 10s so user isn't stuck
    const safetyTimeoutId = setTimeout(() => {
      setAdminCheckComplete(true);
    }, 10000);

    // Construct admin document path
    const adminPath = `artifacts/${appId}/admins/${user.uid}`;
    console.log(`[Admin] Attempt #${attemptNumber} - Checking admin status at path: ${adminPath}`);
    console.log(`[Admin] User ID: ${user.uid}`);
    console.log(`[Admin] User email: ${user.email}`);
    console.log(`[Admin] App ID: ${appId}`);
    
    const adminRef = doc(db, 'artifacts', appId, 'admins', user.uid);
    
    // First, try a direct fetch to verify document exists by UID
    const checkAdminDirect = async () => {
      try {
        console.log('[Admin] Attempting direct fetch of admin document by UID...');
        const adminSnap = await getDoc(adminRef);
        
        console.log('[Admin] Direct fetch completed');
        console.log('[Admin] Document exists:', adminSnap.exists());
        
        if (adminSnap.exists()) {
          const adminData = adminSnap.data() as AdminData;
          console.log('[Admin] Direct fetch - Admin data:', {
            role: adminData.role,
            isActive: adminData.isActive,
            email: adminData.email,
            displayName: adminData.displayName,
          });
          
          if (adminData.isActive) {
            console.log(`[Admin] ✅ Direct fetch confirmed: User is active ${adminData.role} admin`);
            setIsAdmin(true);
            setAdminRole(adminData.role);
            setAdminAllowedModules(adminData.allowedModules ?? null);
            return true; // Found by UID
          } else {
            console.log('[Admin] ⚠️ Direct fetch: User is admin but not active');
            setIsAdmin(false);
            setAdminRole(null);
            setAdminAllowedModules(null);
            return false;
          }
        } else {
          // Document not found by UID, try querying by email
          console.log('[Admin] ℹ️ No admin document found by UID, querying by email...');
          
          if (!user.email) {
            console.log('[Admin] ⚠️ User has no email, cannot query by email');
            setIsAdmin(false);
            setAdminRole(null);
            setAdminAllowedModules(null);
            return false;
          }
          
          // Query admins collection by email
          const adminsRef = collection(db, 'artifacts', appId, 'admins');
          const emailQuery = query(adminsRef, where('email', '==', user.email));
          const emailQuerySnapshot = await getDocs(emailQuery);
          
          if (!emailQuerySnapshot.empty) {
            // Found admin document by email
            const emailDoc = emailQuerySnapshot.docs[0];
            const adminData = emailDoc.data() as AdminData;
            
            console.log('[Admin] ✅ Found admin document by email:', {
              docId: emailDoc.id,
              role: adminData.role,
              isActive: adminData.isActive,
              email: adminData.email,
              displayName: adminData.displayName,
            });
            
            if (adminData.isActive) {
              // Migrate document to use UID as document ID for better performance
              console.log('[Admin] Migrating admin document to use UID as document ID...');
              try {
                await setDoc(adminRef, {
                  ...adminData,
                  updatedAt: serverTimestamp(),
                  updatedBy: user.uid,
                });
                
                // Delete the old document if it's not already the UID-based one
                if (emailDoc.id !== user.uid) {
                  await deleteDoc(doc(db, 'artifacts', appId, 'admins', emailDoc.id));
                  console.log('[Admin] ✅ Migrated admin document to UID-based ID');
                }
              } catch (migrationError) {
                console.error('[Admin] ⚠️ Error migrating admin document:', migrationError);
                // Continue anyway - we found the admin, just couldn't migrate
              }
              
              console.log(`[Admin] ✅ User is active ${adminData.role} admin (found by email)`);
              setIsAdmin(true);
              setAdminRole(adminData.role);
              setAdminAllowedModules(adminData.allowedModules ?? null);
              return true;
            } else {
              console.log('[Admin] ⚠️ User is admin but not active (found by email)');
              setIsAdmin(false);
              setAdminRole(null);
              setAdminAllowedModules(null);
              return false;
            }
          } else {
            console.log('[Admin] ℹ️ No admin document found by email either');
            setIsAdmin(false);
            setAdminRole(null);
            setAdminAllowedModules(null);
            return false;
          }
        }
      } catch (error) {
        console.error('[Admin] ❌ Direct fetch error:', error);
        setIsAdmin(false);
        setAdminRole(null);
        setAdminAllowedModules(null);
        return false;
      }
    };
    
    // Execute direct check first, then set up listener after migration completes
    checkAdminDirect().then(() => {
      clearTimeout(safetyTimeoutId);
      setAdminCheckComplete(true);
      // Set up real-time listener for admin status changes
      // The document should now be at the UID path after migration (if found)
      console.log('[Admin] Setting up real-time listener for admin status');
      
      adminUnsubscribeRef.current = onSnapshot(
        adminRef,
        (docSnap) => {
          console.log('[Admin] Listener snapshot received');
          console.log('[Admin] Document exists:', docSnap.exists());
          console.log('[Admin] Document path:', docSnap.ref.path);
          
          if (docSnap.exists()) {
            const adminData = docSnap.data() as AdminData;
            console.log('[Admin] Listener - Admin data:', {
              role: adminData.role,
              isActive: adminData.isActive,
              email: adminData.email,
              displayName: adminData.displayName,
            });
            
            if (adminData.isActive) {
              console.log(`[Admin] ✅ Listener confirmed: User is active ${adminData.role} admin`);
              setIsAdmin(true);
              setAdminRole(adminData.role);
              setAdminAllowedModules(adminData.allowedModules ?? null);
            } else {
              console.log('[Admin] ⚠️ Listener: User is admin but not active');
              setIsAdmin(false);
              setAdminRole(null);
              setAdminAllowedModules(null);
            }
          } else {
            // Document doesn't exist at UID path - might need to query by email again
            console.log('[Admin] ℹ️ Listener: No admin document found at UID path');
            if (user.email) {
              // Try querying by email as fallback
              const adminsRef = collection(db, 'artifacts', appId, 'admins');
              const emailQuery = query(adminsRef, where('email', '==', user.email));
              getDocs(emailQuery).then((snapshot) => {
                if (!snapshot.empty) {
                  const emailDoc = snapshot.docs[0];
                  const adminData = emailDoc.data() as AdminData;
                  if (adminData.isActive) {
                    console.log(`[Admin] ✅ Found active admin by email via listener fallback`);
                    setIsAdmin(true);
                    setAdminRole(adminData.role);
                    setAdminAllowedModules(adminData.allowedModules ?? null);
                  } else {
                    setIsAdmin(false);
                    setAdminRole(null);
                    setAdminAllowedModules(null);
                  }
                } else {
                  setIsAdmin(false);
                  setAdminRole(null);
                  setAdminAllowedModules(null);
                }
              }).catch((error) => {
                console.error('[Admin] Error querying by email in listener fallback:', error);
                setIsAdmin(false);
                setAdminRole(null);
                setAdminAllowedModules(null);
              });
            } else {
              setIsAdmin(false);
              setAdminRole(null);
              setAdminAllowedModules(null);
            }
          }
        },
        (error) => {
          console.error('[Admin] ❌ Listener error:', error);
          console.error('[Admin] Error code:', error.code);
          console.error('[Admin] Error message:', error.message);
          console.error('[Admin] Full error:', JSON.stringify(error, null, 2));

          setAdminCheckComplete(true);
          // On listener error, try direct fetch as fallback
          console.log('[Admin] Listener failed, attempting fallback direct fetch...');
          checkAdminDirect().catch((fallbackError) => {
            console.error('[Admin] Fallback direct fetch also failed:', fallbackError);
            setIsAdmin(false);
            setAdminRole(null);
          });
        }
      );
    }).catch((error) => {
      clearTimeout(safetyTimeoutId);
      console.error('[Admin] Direct check failed:', error);
      setIsAdmin(false);
      setAdminRole(null);
      setAdminCheckComplete(true);
    });

    return () => {
      clearTimeout(safetyTimeoutId);
      console.log('[Admin] Cleaning up admin listener');
      if (adminUnsubscribeRef.current) {
        adminUnsubscribeRef.current();
        adminUnsubscribeRef.current = null;
      }
    };
  }, [user]);

  // Profile Fetching - prevent duplicate listeners
  const profileUnsubscribeRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    if (!user) {
      setUserProfile(null);
      // Clean up existing listener
      if (profileUnsubscribeRef.current) {
        profileUnsubscribeRef.current();
        profileUnsubscribeRef.current = null;
      }
      return;
    }
    
    // If listener already exists, don't create a new one
    if (profileUnsubscribeRef.current) {
      return;
    }

    // Check for cached photo first
    const cachedPhotoUrl = getCachedPhoto(user.uid);
    
    const profileRef = doc(db, 'artifacts', appId, 'users', user.uid, 'profile', 'main');
    
    profileUnsubscribeRef.current = onSnapshot(
      profileRef,
      (docSnap) => {
        let finalPhotoUrl: string | null = null;

        if (docSnap.exists()) {
          const profileData = docSnap.data() as UserProfile;
          finalPhotoUrl = profileData.photoUrl || user.photoURL || null;
          
          setUserProfile({
            displayName: profileData.displayName || user.displayName || 'Anonymous',
            photoUrl: cachedPhotoUrl || finalPhotoUrl,
          });
        } else {
          if (user.displayName || user.photoURL) {
            finalPhotoUrl = user.photoURL || null;
            
            setUserProfile({
              displayName: user.displayName || 'Anonymous',
              photoUrl: cachedPhotoUrl || finalPhotoUrl,
            });
          } else {
            setUserProfile(null);
          }
        }

        // Update in-memory photo cache and eagerly pre-fetch blob into localStorage
        // so Avatar renders instantly from local storage on all subsequent page loads.
        if (finalPhotoUrl) {
          updateCachedPhoto(user.uid, finalPhotoUrl);
          fetchAndCachePhotoDataUrl(finalPhotoUrl).catch(() => {
            // silent — Avatar falls back to the remote URL if this fails
          });
        }
      },
      (error) => {
        console.error('[Profile] Error:', error);
        if (user.displayName || user.photoURL) {
          const fallbackPhotoUrl = cachedPhotoUrl || user.photoURL || null;
          
          setUserProfile({
            displayName: user.displayName || 'Anonymous',
            photoUrl: fallbackPhotoUrl,
          });
        }
      }
    );
    
    return () => {
      if (profileUnsubscribeRef.current) {
        profileUnsubscribeRef.current();
        profileUnsubscribeRef.current = null;
      }
    };
  }, [user]);

  const showNotify = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  useEffect(() => {
    let isCancelled = false;

    const completeClaim = async (): Promise<void> => {
      let claimResult;
      try {
        claimResult = await completeDonorClaimIfPresent(globalThis.location.href);
      } catch (error) {
        if (isCancelled) return;
        const message =
          error instanceof Error
            ? `[Donor Claim] ${error.message}`
            : '[Donor Claim] Unable to complete account claim right now.';
        setNotification({ message, type: 'error' });
        setTimeout(() => setNotification(null), 5000);
        return;
      }
      if (!claimResult.processed || isCancelled) return;

      setNotification({
        message: claimResult.message,
        type: claimResult.success ? 'success' : 'error',
      });
      setTimeout(() => setNotification(null), 5000);

      if (claimResult.success) {
        globalThis.history.replaceState({}, '', '/dashboard/donor?claim=success');
      }
    };

    void completeClaim();

    return () => {
      isCancelled = true;
    };
  }, [location.pathname, location.search]);

  const confirmAction = (title: string, message: string, action: () => void) => {
    setConfirmation({ isOpen: true, title, message, onConfirm: action });
  };

  /**
   * Upserts customer metadata onto the users/{uid} parent document so admins
   * can manage non-admin signed-up users. The parent document is otherwise an
   * empty container shell — writing here keeps everything under the existing
   * users collection without introducing a separate customers collection.
   * Uses `merge: true` so subcollections (profile, federalReadinessScan) are untouched.
   */
  const upsertCustomerRecord = async (
    uid: string,
    email: string | null,
    displayName: string,
    photoUrl: string | null,
    signInMethod: 'google' | 'email'
  ) => {
    const userRef = doc(db, 'artifacts', appId, 'users', uid);
    await setDoc(
      userRef,
      {
        uid,
        email: email || '',
        displayName,
        photoUrl,
        signInMethod,
        isCustomer: true,
        isActive: true,
        lastSignInAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
    // Set registeredAt only on the first write (merge won't overwrite existing field)
    const snap = await getDoc(userRef);
    if (!snap.data()?.registeredAt) {
      await setDoc(userRef, { registeredAt: serverTimestamp() }, { merge: true });
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      // Check if already signed in with Google (not anonymous)
      if (user && !user.isAnonymous) {
        const cachedSessionId = getCachedSession(user.uid);
        if (cachedSessionId) {
          console.log('[Auth] User already signed in with Google');
          showNotify('You are already signed in', 'info');
          return;
        }
      }

      // Check rate limiting before attempting sign-in
      if (isRateLimited()) {
        const resetTime = getRateLimitResetTime();
        const timeDisplay = formatRateLimitTime(resetTime);
        showNotify(
          `Too many sign-in attempts. Please wait ${timeDisplay} before trying again.`,
          'error'
        );
        console.warn('[Auth] Rate limit exceeded for Google sign-in');
        return;
      }

      // Record this attempt for rate limiting
      recordSignInAttempt();

      // Proceed with Google sign-in
      const result = await signInWithPopup(auth, googleProvider);
      const signedInUser = result.user;
      
      let photoUrl: string | null = null;
      if (signedInUser.photoURL) {
        photoUrl = signedInUser.photoURL;
      }
      
      await setDoc(
        doc(db, 'artifacts', appId, 'users', signedInUser.uid, 'profile', 'main'),
        {
          displayName: signedInUser.displayName || 'Anonymous',
          photoUrl,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      // Upsert customer record for admin panel
      await upsertCustomerRecord(
        signedInUser.uid,
        signedInUser.email,
        signedInUser.displayName || 'Anonymous',
        photoUrl,
        'google'
      );

      // Cache the successful session
      cacheSession(signedInUser.uid);

      // Cache the user's photo in memory and eagerly fetch blob into localStorage.
      // This means the Avatar will already have a local data URL before it first renders.
      if (photoUrl) {
        cachePhoto(signedInUser.uid, photoUrl);
        fetchAndCachePhotoDataUrl(photoUrl).catch(() => {
          // silent — Avatar falls back gracefully
        });
      }
      
      showNotify('Signed in with Google successfully', 'success');
    } catch (error: unknown) {
      console.error('[Auth] Google sign-in error:', error);
      const firebaseError = error as { code?: string; message?: string };
      
      if (firebaseError.code === 'auth/popup-closed-by-user') {
        showNotify('Sign-in cancelled', 'info');
      } else if (firebaseError.code === 'auth/too-many-requests') {
        // Handle Firebase rate limiting specifically
        const resetTime = getRateLimitResetTime();
        const timeDisplay = formatRateLimitTime(resetTime);
        showNotify(
          `Too many sign-in requests. Please wait ${timeDisplay} before trying again.`,
          'error'
        );
      } else if (firebaseError.code === 'auth/network-request-failed') {
        showNotify('Network error. Please check your connection and try again.', 'error');
      } else {
        showNotify('Failed to sign in with Google. Please try again later.', 'error');
      }
    }
  };

  const handleEmailSignIn = async (email: string, password: string) => {
    try {
      // Check rate limiting before attempting sign-in
      if (isRateLimited()) {
        const resetTime = getRateLimitResetTime();
        const timeDisplay = formatRateLimitTime(resetTime);
        showNotify(
          `Too many sign-in attempts. Please wait ${timeDisplay} before trying again.`,
          'error'
        );
        console.warn('[Auth] Rate limit exceeded for email sign-in');
        return;
      }

      // Record this attempt for rate limiting
      recordSignInAttempt();

      // Sign in with email and password
      const result = await signInWithEmailAndPassword(auth, email, password);
      const signedInUser = result.user;

      // Create or update user profile in Firestore
      await setDoc(
        doc(db, 'artifacts', appId, 'users', signedInUser.uid, 'profile', 'main'),
        {
          displayName: signedInUser.displayName || signedInUser.email?.split('@')[0] || 'User',
          photoUrl: null,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      // Upsert customer record for admin panel
      await upsertCustomerRecord(
        signedInUser.uid,
        signedInUser.email,
        signedInUser.displayName || signedInUser.email?.split('@')[0] || 'User',
        null,
        'email'
      );

      // Cache the successful session
      cacheSession(signedInUser.uid);

      showNotify('Signed in successfully', 'success');
    } catch (error: unknown) {
      console.error('[Auth] Email sign-in error:', error);
      const firebaseError = error as { code?: string; message?: string };

      if (firebaseError.code === 'auth/invalid-credential' || firebaseError.code === 'auth/user-not-found' || firebaseError.code === 'auth/wrong-password') {
        showNotify('Invalid email or password. Please try again.', 'error');
      } else if (firebaseError.code === 'auth/too-many-requests') {
        const resetTime = getRateLimitResetTime();
        const timeDisplay = formatRateLimitTime(resetTime);
        showNotify(
          `Too many sign-in requests. Please wait ${timeDisplay} before trying again.`,
          'error'
        );
      } else if (firebaseError.code === 'auth/network-request-failed') {
        showNotify('Network error. Please check your connection and try again.', 'error');
      } else {
        showNotify('Failed to sign in. Please try again later.', 'error');
      }
    }
  };

  const handleEmailRegister = async (email: string, password: string, displayName: string) => {
    try {
      // Check rate limiting before attempting registration
      if (isRateLimited()) {
        const resetTime = getRateLimitResetTime();
        const timeDisplay = formatRateLimitTime(resetTime);
        showNotify(
          `Too many sign-in attempts. Please wait ${timeDisplay} before trying again.`,
          'error'
        );
        console.warn('[Auth] Rate limit exceeded for email registration');
        return;
      }

      // Record this attempt for rate limiting
      recordSignInAttempt();

      // Create user with email and password
      const result = await createUserWithEmailAndPassword(auth, email, password);
      const newUser = result.user;

      // Send email verification
      try {
        await sendEmailVerification(newUser);
        console.log('[Auth] Verification email sent to:', email);
      } catch (verifyError) {
        console.error('[Auth] Failed to send verification email:', verifyError);
        // Continue even if verification email fails
      }

      // Create user profile in Firestore
      await setDoc(
        doc(db, 'artifacts', appId, 'users', newUser.uid, 'profile', 'main'),
        {
          displayName: displayName.trim(),
          photoUrl: null,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      // Upsert customer record for admin panel
      await upsertCustomerRecord(
        newUser.uid,
        newUser.email,
        displayName.trim(),
        null,
        'email'
      );

      // Cache the successful session
      cacheSession(newUser.uid);

      showNotify(
        'Account created successfully! Please check your email to verify your account.',
        'success'
      );
    } catch (error: unknown) {
      console.error('[Auth] Email registration error:', error);
      const firebaseError = error as { code?: string; message?: string };

      if (firebaseError.code === 'auth/email-already-in-use') {
        showNotify('This email is already registered. Please sign in instead.', 'error');
      } else if (firebaseError.code === 'auth/invalid-email') {
        showNotify('Invalid email address. Please check and try again.', 'error');
      } else if (firebaseError.code === 'auth/weak-password') {
        showNotify('Password is too weak. Please use a stronger password.', 'error');
      } else if (firebaseError.code === 'auth/too-many-requests') {
        const resetTime = getRateLimitResetTime();
        const timeDisplay = formatRateLimitTime(resetTime);
        showNotify(
          `Too many registration requests. Please wait ${timeDisplay} before trying again.`,
          'error'
        );
      } else if (firebaseError.code === 'auth/network-request-failed') {
        showNotify('Network error. Please check your connection and try again.', 'error');
      } else {
        showNotify('Failed to create account. Please try again later.', 'error');
      }
    }
  };

  const handlePasswordReset = async (email: string) => {
    try {
      // Check rate limiting before attempting password reset
      if (isRateLimited()) {
        const resetTime = getRateLimitResetTime();
        const timeDisplay = formatRateLimitTime(resetTime);
        showNotify(
          `Too many requests. Please wait ${timeDisplay} before trying again.`,
          'error'
        );
        console.warn('[Auth] Rate limit exceeded for password reset');
        return;
      }

      // Record this attempt for rate limiting
      recordSignInAttempt();

      // Send password reset email
      await sendPasswordResetEmail(auth, email);

      showNotify(
        'Password reset email sent! Please check your inbox and follow the instructions.',
        'success'
      );
      console.log('[Auth] Password reset email sent to:', email);
    } catch (error: unknown) {
      console.error('[Auth] Password reset error:', error);
      const firebaseError = error as { code?: string; message?: string };

      if (firebaseError.code === 'auth/user-not-found') {
        // Don't reveal if user exists (security best practice)
        showNotify(
          'If an account exists with this email, you will receive a password reset link.',
          'info'
        );
      } else if (firebaseError.code === 'auth/invalid-email') {
        showNotify('Invalid email address. Please check and try again.', 'error');
      } else if (firebaseError.code === 'auth/too-many-requests') {
        const resetTime = getRateLimitResetTime();
        const timeDisplay = formatRateLimitTime(resetTime);
        showNotify(
          `Too many reset requests. Please wait ${timeDisplay} before trying again.`,
          'error'
        );
      } else if (firebaseError.code === 'auth/network-request-failed') {
        showNotify('Network error. Please check your connection and try again.', 'error');
      } else {
        showNotify('Failed to send reset email. Please try again later.', 'error');
      }
    }
  };

  const handleSignOut = async () => {
    try {
      // Clear cached session and photo before signing out
      if (user) {
        clearSessionCache(user.uid);
        clearPhotoCache(user.uid);
      }

      await firebaseSignOut(auth);
      setIsAdmin(false);
      setAdminAllowedModules(null);
      showNotify('Signed out successfully', 'info');
    } catch (error) {
      console.error('[Auth] Sign-out error:', error);
      showNotify('Failed to sign out', 'error');
    }
  };

  const handleSaveProfile = async (displayName: string) => {
    if (!user) return;
    const photoUrl = user.photoURL || userProfile?.photoUrl || null;
    await setDoc(
      doc(db, 'artifacts', appId, 'users', user.uid, 'profile', 'main'),
      { displayName, photoUrl, updatedAt: serverTimestamp() },
      { merge: true }
    );
  };

  const navigate = useNavigate();

  const handleProfileClick = () => {
    if (user && !user.isAnonymous) {
      navigate('/dashboard/profile');
      setIsProfileOpen(false);
      return;
    }
    setIsProfileOpen(true);
  };

  useEffect(() => {
    if (showAdminPanel && (!user || !isAdmin || !adminRole)) {
      setShowAdminPanel(false);
      showNotify('Access denied. Admin privileges required.', 'error');
    }
  }, [showAdminPanel, user, isAdmin, adminRole]);

  // Global handler: CMS content "Book a Strategy Call" links navigate to the strategy call page
  useEffect(() => {
    const goToStrategyCallPage = () => navigate('/book-a-call');

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const link = target.closest('a[href="#open-strategy-call"]');
      const button = target.closest('[data-action="open-strategy-call"]');
      if (link || button) {
        e.preventDefault();
        goToStrategyCallPage();
      }
    };

    const handleHashChange = () => {
      if (window.location.hash === '#open-strategy-call') {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
        goToStrategyCallPage();
      }
    };

    document.addEventListener('click', handleClick, true);
    window.addEventListener('hashchange', handleHashChange);
    if (window.location.hash === '#open-strategy-call') handleHashChange();

    return () => {
      document.removeEventListener('click', handleClick, true);
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [navigate]);

  // Scroll to top when route changes so new pages (e.g. About) start at top
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Substitute {{phone-number}}, {{contact-email}}, etc. in custom header/footer HTML
  const placeholderReplacements = getPlaceholderReplacements(contact, branding);
  const substitutedHeaderHtml = headerHtml ? replaceCmsPlaceholders(headerHtml, placeholderReplacements) : null;
  const substitutedFooterHtml = footerHtml ? replaceCmsPlaceholders(footerHtml, placeholderReplacements) : null;

  // Shared nav props threaded into every Header and Footer
  const sharedHeaderProps = {
    navItems,
    navGroups,
    headerConfig,
    headerHtml: substitutedHeaderHtml,
    placeholderReplacements,
    onSignOut: handleSignOut,
  };
  const sharedFooterProps = {
    navItems,
    navGroups,
    footerConfig,
    footerHtml: substitutedFooterHtml,
    placeholderReplacements,
  };
  const showUrgentBanner = !location.pathname.startsWith('/admin');

  return (
    <CookieConsentProvider>
      <AnalyticsScripts />
      <OfflineNotice isOnline={isOnline} />
      {showUrgentBanner ? <UrgentBanner config={urgentBanner} /> : null}
      <CookieConsentBanner />
      <Toast notification={notification} onClose={() => setNotification(null)} />
      <ConfirmationModal
        isOpen={confirmation.isOpen}
        title={confirmation.title}
        message={confirmation.message}
        onConfirm={confirmation.onConfirm}
        onClose={() => setConfirmation((prev) => ({ ...prev, isOpen: false }))}
      />
      <Routes>
        <Route
          path="/admin/:section?"
          element={
            !authReady || (user && !adminCheckComplete) ? (
              <div className="min-h-screen flex items-center justify-center bg-gray-50" aria-live="polite" aria-busy="true">
                <p className="text-gray-600">Loading…</p>
              </div>
            ) : authReady && adminCheckComplete && user && isAdmin && adminRole ? (
              <AdminPanel
                currentUser={user}
                currentUserRole={adminRole}
                allowedModules={adminAllowedModules}
                onBackToHome={() => navigate('/')}
                userProfile={userProfile}
                showNotify={showNotify}
                confirmAction={confirmAction}
                onSaveProfile={handleSaveProfile}
                onGoogleSignIn={handleGoogleSignIn}
                onEmailSignIn={handleEmailSignIn}
                onEmailRegister={handleEmailRegister}
                onPasswordReset={handlePasswordReset}
                onSignOut={handleSignOut}
                allowAnonymousSignIn={allowAnonymousSignIn ?? true}
              />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/dashboard"
          element={
            !authReady ? (
              <div className="min-h-screen flex items-center justify-center bg-gray-50" aria-live="polite" aria-busy="true">
                <p className="text-gray-600">Loading…</p>
              </div>
            ) : user && !user.isAnonymous ? (
              <PageShell>
                <Header
                  user={user}
                  userProfile={userProfile}
                  onProfileClick={handleProfileClick}
                  branding={branding || undefined}
                  contact={contact || undefined}
                  isAdmin={!!(user && isAdmin && adminRole)}
                  onOpenStrategyCall={() => navigate('/book-a-call')}
                  {...sharedHeaderProps}
                />
                <PageMain>
                <CustomerDashboardPage
                  displayName={userProfile?.displayName || user.displayName || 'Supporter'}
                  userId={user.uid}
                />
                </PageMain>
                <Footer brandName={branding?.brandName} brandTagline={branding?.brandTagline} contact={contact || undefined} {...sharedFooterProps} />
                <ProfileModal
                  isOpen={isProfileOpen}
                  onClose={() => setIsProfileOpen(false)}
                  user={user}
                  userProfile={userProfile}
                  onSaveProfile={handleSaveProfile}
                  onGoogleSignIn={handleGoogleSignIn}
                  onEmailSignIn={handleEmailSignIn}
                  onEmailRegister={handleEmailRegister}
                  onPasswordReset={handlePasswordReset}
                  onSignOut={handleSignOut}
                  onOpenAdminPanel={() => {
                    if (isAdmin && user && adminRole) {
                      navigate('/admin');
                      setIsProfileOpen(false);
                    }
                  }}
                  isAdmin={isAdmin}
                  adminRole={adminRole}
                  showNotify={showNotify}
                  allowAnonymousSignIn={allowAnonymousSignIn ?? true}
                />
                <ScrollToTop />
              </PageShell>
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/dashboard/donor"
          element={
            !authReady ? (
              <div className="min-h-screen flex items-center justify-center bg-gray-50" aria-live="polite" aria-busy="true">
                <p className="text-gray-600">Loading…</p>
              </div>
            ) : user && !user.isAnonymous ? (
              <PageShell>
                <Header
                  user={user}
                  userProfile={userProfile}
                  onProfileClick={handleProfileClick}
                  branding={branding || undefined}
                  contact={contact || undefined}
                  isAdmin={!!(user && isAdmin && adminRole)}
                  onOpenStrategyCall={() => navigate('/book-a-call')}
                  {...sharedHeaderProps}
                />
                <PageMain>
                <DonorDashboardPage
                  userId={user.uid}
                  displayName={userProfile?.displayName || user.displayName || 'Donor'}
                />
                </PageMain>
                <Footer brandName={branding?.brandName} brandTagline={branding?.brandTagline} contact={contact || undefined} {...sharedFooterProps} />
                <ProfileModal
                  isOpen={isProfileOpen}
                  onClose={() => setIsProfileOpen(false)}
                  user={user}
                  userProfile={userProfile}
                  onSaveProfile={handleSaveProfile}
                  onGoogleSignIn={handleGoogleSignIn}
                  onEmailSignIn={handleEmailSignIn}
                  onEmailRegister={handleEmailRegister}
                  onPasswordReset={handlePasswordReset}
                  onSignOut={handleSignOut}
                  onOpenAdminPanel={() => {
                    if (isAdmin && user && adminRole) {
                      navigate('/admin');
                      setIsProfileOpen(false);
                    }
                  }}
                  isAdmin={isAdmin}
                  adminRole={adminRole}
                  showNotify={showNotify}
                  allowAnonymousSignIn={allowAnonymousSignIn ?? true}
                />
                <ScrollToTop />
              </PageShell>
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/dashboard/profile"
          element={
            !authReady ? (
              <div className="min-h-screen flex items-center justify-center bg-gray-50" aria-live="polite" aria-busy="true">
                <p className="text-gray-600">Loading…</p>
              </div>
            ) : user && !user.isAnonymous ? (
              <PageShell>
                <Header
                  user={user}
                  userProfile={userProfile}
                  onProfileClick={handleProfileClick}
                  branding={branding || undefined}
                  contact={contact || undefined}
                  isAdmin={!!(user && isAdmin && adminRole)}
                  onOpenStrategyCall={() => navigate('/book-a-call')}
                  {...sharedHeaderProps}
                />
                <PageMain>
                <CustomerProfilePage
                  userId={user.uid}
                  user={user}
                  userProfile={userProfile}
                  isAdmin={isAdmin}
                  defaultDisplayName={userProfile?.displayName ?? user.displayName ?? ''}
                  defaultEmail={user.email}
                  onSignOut={handleSignOut}
                  showNotify={showNotify}
                />
                </PageMain>
                <Footer brandName={branding?.brandName} brandTagline={branding?.brandTagline} contact={contact || undefined} {...sharedFooterProps} />
                <ProfileModal
                  isOpen={isProfileOpen}
                  onClose={() => setIsProfileOpen(false)}
                  user={user}
                  userProfile={userProfile}
                  onSaveProfile={handleSaveProfile}
                  onGoogleSignIn={handleGoogleSignIn}
                  onEmailSignIn={handleEmailSignIn}
                  onEmailRegister={handleEmailRegister}
                  onPasswordReset={handlePasswordReset}
                  onSignOut={handleSignOut}
                  onOpenAdminPanel={() => {
                    if (isAdmin && user && adminRole) {
                      navigate('/admin');
                      setIsProfileOpen(false);
                    }
                  }}
                  isAdmin={isAdmin}
                  adminRole={adminRole}
                  showNotify={showNotify}
                  allowAnonymousSignIn={allowAnonymousSignIn ?? true}
                />
                <ScrollToTop />
              </PageShell>
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/dashboard/federal-readiness-results"
          element={
            !authReady ? (
              <div className="min-h-screen flex items-center justify-center bg-gray-50" aria-live="polite" aria-busy="true">
                <p className="text-gray-600">Loading…</p>
              </div>
            ) : user && !user.isAnonymous ? (
              <PageShell>
                <Header
                  user={user}
                  userProfile={userProfile}
                  onProfileClick={handleProfileClick}
                  branding={branding || undefined}
                  contact={contact || undefined}
                  isAdmin={!!(user && isAdmin && adminRole)}
                  onOpenStrategyCall={() => navigate('/book-a-call')}
                  {...sharedHeaderProps}
                />
                <PageMain>
                <FederalReadinessResultsPage userId={user.uid} />
                </PageMain>
                <Footer brandName={branding?.brandName} brandTagline={branding?.brandTagline} contact={contact || undefined} {...sharedFooterProps} />
                <ProfileModal
                  isOpen={isProfileOpen}
                  onClose={() => setIsProfileOpen(false)}
                  user={user}
                  userProfile={userProfile}
                  onSaveProfile={handleSaveProfile}
                  onGoogleSignIn={handleGoogleSignIn}
                  onEmailSignIn={handleEmailSignIn}
                  onEmailRegister={handleEmailRegister}
                  onPasswordReset={handlePasswordReset}
                  onSignOut={handleSignOut}
                  onOpenAdminPanel={() => {
                    if (isAdmin && user && adminRole) {
                      navigate('/admin');
                      setIsProfileOpen(false);
                    }
                  }}
                  isAdmin={isAdmin}
                  adminRole={adminRole}
                  showNotify={showNotify}
                  allowAnonymousSignIn={allowAnonymousSignIn ?? true}
                />
                <ScrollToTop />
              </PageShell>
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/dashboard/billing"
          element={
            !authReady ? (
              <div className="min-h-screen flex items-center justify-center bg-gray-50" aria-live="polite" aria-busy="true">
                <p className="text-gray-600">Loading…</p>
              </div>
            ) : user && !user.isAnonymous ? (
              <PageShell>
                <Header
                  user={user}
                  userProfile={userProfile}
                  onProfileClick={handleProfileClick}
                  branding={branding || undefined}
                  contact={contact || undefined}
                  isAdmin={!!(user && isAdmin && adminRole)}
                  onOpenStrategyCall={() => navigate('/book-a-call')}
                  {...sharedHeaderProps}
                />
                <PageMain>
                <CustomerBillingPage
                  userId={user.uid}
                />
                </PageMain>
                <Footer brandName={branding?.brandName} brandTagline={branding?.brandTagline} contact={contact || undefined} {...sharedFooterProps} />
                <ProfileModal
                  isOpen={isProfileOpen}
                  onClose={() => setIsProfileOpen(false)}
                  user={user}
                  userProfile={userProfile}
                  onSaveProfile={handleSaveProfile}
                  onGoogleSignIn={handleGoogleSignIn}
                  onEmailSignIn={handleEmailSignIn}
                  onEmailRegister={handleEmailRegister}
                  onPasswordReset={handlePasswordReset}
                  onSignOut={handleSignOut}
                  onOpenAdminPanel={() => {
                    if (isAdmin && user && adminRole) {
                      navigate('/admin');
                      setIsProfileOpen(false);
                    }
                  }}
                  isAdmin={isAdmin}
                  adminRole={adminRole}
                  showNotify={showNotify}
                  allowAnonymousSignIn={allowAnonymousSignIn ?? true}
                />
                <ScrollToTop />
              </PageShell>
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/dashboard/services"
          element={
            !authReady ? (
              <div className="min-h-screen flex items-center justify-center bg-gray-50" aria-live="polite" aria-busy="true">
                <p className="text-gray-600">Loading…</p>
              </div>
            ) : user && !user.isAnonymous ? (
              <PageShell>
                <Header
                  user={user}
                  userProfile={userProfile}
                  onProfileClick={handleProfileClick}
                  branding={branding || undefined}
                  contact={contact || undefined}
                  isAdmin={!!(user && isAdmin && adminRole)}
                  onOpenStrategyCall={() => navigate('/book-a-call')}
                  {...sharedHeaderProps}
                />
                <PageMain>
                <CustomerServicesPage showNotify={showNotify} />
                </PageMain>
                <Footer brandName={branding?.brandName} brandTagline={branding?.brandTagline} contact={contact || undefined} {...sharedFooterProps} />
                <ProfileModal
                  isOpen={isProfileOpen}
                  onClose={() => setIsProfileOpen(false)}
                  user={user}
                  userProfile={userProfile}
                  onSaveProfile={handleSaveProfile}
                  onGoogleSignIn={handleGoogleSignIn}
                  onEmailSignIn={handleEmailSignIn}
                  onEmailRegister={handleEmailRegister}
                  onPasswordReset={handlePasswordReset}
                  onSignOut={handleSignOut}
                  onOpenAdminPanel={() => {
                    if (isAdmin && user && adminRole) {
                      navigate('/admin');
                      setIsProfileOpen(false);
                    }
                  }}
                  isAdmin={isAdmin}
                  adminRole={adminRole}
                  showNotify={showNotify}
                  allowAnonymousSignIn={allowAnonymousSignIn ?? true}
                />
                <ScrollToTop />
              </PageShell>
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route path="/corporate-partners" element={<Navigate to="/partners" replace />} />
        <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
        <Route path="/terms" element={<Navigate to="/terms-of-service" replace />} />
        <Route path="/about" element={<Navigate to="/about-us" replace />} />
        <Route
          path="/lp/:slug"
          element={
            <PageShell>
              <Header
                user={user}
                userProfile={userProfile}
                onProfileClick={handleProfileClick}
                branding={branding || undefined}
                contact={contact || undefined}
                isAdmin={!!(user && isAdmin && adminRole)}
                onOpenStrategyCall={() => navigate('/book-a-call')}
                {...sharedHeaderProps}
              />
              <PageMain>
              <CMSPageViewRoute brandName={branding?.brandName} />
              </PageMain>
              <Footer brandName={branding?.brandName} brandTagline={branding?.brandTagline} contact={contact || undefined} {...sharedFooterProps} />
              <ProfileModal
                isOpen={isProfileOpen}
                onClose={() => setIsProfileOpen(false)}
                user={user}
                userProfile={userProfile}
                onSaveProfile={handleSaveProfile}
                onGoogleSignIn={handleGoogleSignIn}
                onEmailSignIn={handleEmailSignIn}
                onEmailRegister={handleEmailRegister}
                onPasswordReset={handlePasswordReset}
                onSignOut={handleSignOut}
                onOpenAdminPanel={() => {
                  if (isAdmin && user && adminRole) {
                    navigate('/admin');
                    setIsProfileOpen(false);
                  }
                }}
                isAdmin={isAdmin}
                adminRole={adminRole}
                showNotify={showNotify}
                allowAnonymousSignIn={allowAnonymousSignIn ?? true}
              />
              <ScrollToTop />
            </PageShell>
          }
        />
        <Route
          path="/articles"
          element={
            <PageShell>
              <Header
                user={user}
                userProfile={userProfile}
                onProfileClick={handleProfileClick}
                branding={branding || undefined}
                contact={contact || undefined}
                isAdmin={!!(user && isAdmin && adminRole)}
                onOpenStrategyCall={() => navigate('/book-a-call')}
                {...sharedHeaderProps}
              />
              <PageMain>
              <AllArticlesPage />
              </PageMain>
              <Footer brandName={branding?.brandName} brandTagline={branding?.brandTagline} contact={contact || undefined} {...sharedFooterProps} />
              <ProfileModal
                isOpen={isProfileOpen}
                onClose={() => setIsProfileOpen(false)}
                user={user}
                userProfile={userProfile}
                onSaveProfile={handleSaveProfile}
                onGoogleSignIn={handleGoogleSignIn}
                onEmailSignIn={handleEmailSignIn}
                onEmailRegister={handleEmailRegister}
                onPasswordReset={handlePasswordReset}
                onSignOut={handleSignOut}
                onOpenAdminPanel={() => {
                  if (isAdmin && user && adminRole) {
                    navigate('/admin');
                    setIsProfileOpen(false);
                  }
                }}
                isAdmin={isAdmin}
                adminRole={adminRole}
                showNotify={showNotify}
                allowAnonymousSignIn={allowAnonymousSignIn ?? true}
              />
              <ScrollToTop />
            </PageShell>
          }
        />
        <Route
          path="/blog"
          element={
            <PageShell>
              <Header
                user={user}
                userProfile={userProfile}
                onProfileClick={handleProfileClick}
                branding={branding || undefined}
                contact={contact || undefined}
                isAdmin={!!(user && isAdmin && adminRole)}
                onOpenStrategyCall={() => navigate('/book-a-call')}
                {...sharedHeaderProps}
              />
              <PageMain>
              <AllArticlesPage />
              </PageMain>
              <Footer brandName={branding?.brandName} brandTagline={branding?.brandTagline} contact={contact || undefined} {...sharedFooterProps} />
              <ProfileModal
                isOpen={isProfileOpen}
                onClose={() => setIsProfileOpen(false)}
                user={user}
                userProfile={userProfile}
                onSaveProfile={handleSaveProfile}
                onGoogleSignIn={handleGoogleSignIn}
                onEmailSignIn={handleEmailSignIn}
                onEmailRegister={handleEmailRegister}
                onPasswordReset={handlePasswordReset}
                onSignOut={handleSignOut}
                onOpenAdminPanel={() => {
                  if (isAdmin && user && adminRole) {
                    navigate('/admin');
                    setIsProfileOpen(false);
                  }
                }}
                isAdmin={isAdmin}
                adminRole={adminRole}
                showNotify={showNotify}
                allowAnonymousSignIn={allowAnonymousSignIn ?? true}
              />
              <ScrollToTop />
            </PageShell>
          }
        />
        <Route
          path="/insights/:slug"
          element={
            <PageShell>
              <Header
                user={user}
                userProfile={userProfile}
                onProfileClick={handleProfileClick}
                branding={branding || undefined}
                contact={contact || undefined}
                isAdmin={!!(user && isAdmin && adminRole)}
                onOpenStrategyCall={() => navigate('/book-a-call')}
                {...sharedHeaderProps}
              />
              <PageMain>
              <ArticleDetailPage />
              </PageMain>
              <Footer brandName={branding?.brandName} brandTagline={branding?.brandTagline} contact={contact || undefined} {...sharedFooterProps} />
              <ProfileModal
                isOpen={isProfileOpen}
                onClose={() => setIsProfileOpen(false)}
                user={user}
                userProfile={userProfile}
                onSaveProfile={handleSaveProfile}
                onGoogleSignIn={handleGoogleSignIn}
                onEmailSignIn={handleEmailSignIn}
                onEmailRegister={handleEmailRegister}
                onPasswordReset={handlePasswordReset}
                onSignOut={handleSignOut}
                onOpenAdminPanel={() => {
                  if (isAdmin && user && adminRole) {
                    navigate('/admin');
                    setIsProfileOpen(false);
                  }
                }}
                isAdmin={isAdmin}
                adminRole={adminRole}
                showNotify={showNotify}
                allowAnonymousSignIn={allowAnonymousSignIn ?? true}
              />
              <ScrollToTop />
            </PageShell>
          }
        />
        <Route
          path="/blog/:slug"
          element={
            <PageShell>
              <Header
                user={user}
                userProfile={userProfile}
                onProfileClick={handleProfileClick}
                branding={branding || undefined}
                contact={contact || undefined}
                isAdmin={!!(user && isAdmin && adminRole)}
                onOpenStrategyCall={() => navigate('/book-a-call')}
                {...sharedHeaderProps}
              />
              <PageMain>
              <ArticleDetailPage />
              </PageMain>
              <Footer brandName={branding?.brandName} brandTagline={branding?.brandTagline} contact={contact || undefined} {...sharedFooterProps} />
              <ProfileModal
                isOpen={isProfileOpen}
                onClose={() => setIsProfileOpen(false)}
                user={user}
                userProfile={userProfile}
                onSaveProfile={handleSaveProfile}
                onGoogleSignIn={handleGoogleSignIn}
                onEmailSignIn={handleEmailSignIn}
                onEmailRegister={handleEmailRegister}
                onPasswordReset={handlePasswordReset}
                onSignOut={handleSignOut}
                onOpenAdminPanel={() => {
                  if (isAdmin && user && adminRole) {
                    navigate('/admin');
                    setIsProfileOpen(false);
                  }
                }}
                isAdmin={isAdmin}
                adminRole={adminRole}
                showNotify={showNotify}
                allowAnonymousSignIn={allowAnonymousSignIn ?? true}
              />
              <ScrollToTop />
            </PageShell>
          }
        />
        <Route
          path="/donate"
          element={
            <PageShell>
              <Header
                user={user}
                userProfile={userProfile}
                onProfileClick={handleProfileClick}
                branding={branding || undefined}
                contact={contact || undefined}
                isAdmin={!!(user && isAdmin && adminRole)}
                onOpenStrategyCall={() => navigate('/book-a-call')}
                {...sharedHeaderProps}
              />
              <PageMain>
              <DonatePage
                donorEmail={user && !user.isAnonymous ? user.email ?? undefined : undefined}
                donorDisplayName={
                  user && !user.isAnonymous
                    ? userProfile?.displayName || user.displayName || undefined
                    : undefined
                }
              />
              </PageMain>
              <Footer brandName={branding?.brandName} brandTagline={branding?.brandTagline} contact={contact || undefined} {...sharedFooterProps} />
              <ProfileModal
                isOpen={isProfileOpen}
                onClose={() => setIsProfileOpen(false)}
                user={user}
                userProfile={userProfile}
                onSaveProfile={handleSaveProfile}
                onGoogleSignIn={handleGoogleSignIn}
                onEmailSignIn={handleEmailSignIn}
                onEmailRegister={handleEmailRegister}
                onPasswordReset={handlePasswordReset}
                onSignOut={handleSignOut}
                onOpenAdminPanel={() => {
                  if (isAdmin && user && adminRole) {
                    navigate('/admin');
                    setIsProfileOpen(false);
                  }
                }}
                isAdmin={isAdmin}
                adminRole={adminRole}
                showNotify={showNotify}
                allowAnonymousSignIn={allowAnonymousSignIn ?? true}
              />
              <ScrollToTop />
            </PageShell>
          }
        />
        <Route
          path="/donate/thank-you"
          element={
            <PageShell>
              <Header
                user={user}
                userProfile={userProfile}
                onProfileClick={handleProfileClick}
                branding={branding || undefined}
                contact={contact || undefined}
                isAdmin={!!(user && isAdmin && adminRole)}
                onOpenStrategyCall={() => navigate('/book-a-call')}
                {...sharedHeaderProps}
              />
              <PageMain>
              <DonateThankYouPage isSignedIn={!!(user && !user.isAnonymous)} />
              </PageMain>
              <Footer brandName={branding?.brandName} brandTagline={branding?.brandTagline} contact={contact || undefined} {...sharedFooterProps} />
              <ProfileModal
                isOpen={isProfileOpen}
                onClose={() => setIsProfileOpen(false)}
                user={user}
                userProfile={userProfile}
                onSaveProfile={handleSaveProfile}
                onGoogleSignIn={handleGoogleSignIn}
                onEmailSignIn={handleEmailSignIn}
                onEmailRegister={handleEmailRegister}
                onPasswordReset={handlePasswordReset}
                onSignOut={handleSignOut}
                onOpenAdminPanel={() => {
                  if (isAdmin && user && adminRole) {
                    navigate('/admin');
                    setIsProfileOpen(false);
                  }
                }}
                isAdmin={isAdmin}
                adminRole={adminRole}
                showNotify={showNotify}
                allowAnonymousSignIn={allowAnonymousSignIn ?? true}
              />
              <ScrollToTop />
            </PageShell>
          }
        />
        <Route
          path="/warriors"
          element={
            <PageShell>
              <Header
                user={user}
                userProfile={userProfile}
                onProfileClick={handleProfileClick}
                branding={branding || undefined}
                contact={contact || undefined}
                isAdmin={!!(user && isAdmin && adminRole)}
                onOpenStrategyCall={() => navigate('/book-a-call')}
                {...sharedHeaderProps}
              />
              <PageMain>
              <WarriorsPage />
              </PageMain>
              <Footer brandName={branding?.brandName} brandTagline={branding?.brandTagline} contact={contact || undefined} {...sharedFooterProps} />
              <ProfileModal
                isOpen={isProfileOpen}
                onClose={() => setIsProfileOpen(false)}
                user={user}
                userProfile={userProfile}
                onSaveProfile={handleSaveProfile}
                onGoogleSignIn={handleGoogleSignIn}
                onEmailSignIn={handleEmailSignIn}
                onEmailRegister={handleEmailRegister}
                onPasswordReset={handlePasswordReset}
                onSignOut={handleSignOut}
                onOpenAdminPanel={() => {
                  if (isAdmin && user && adminRole) {
                    navigate('/admin');
                    setIsProfileOpen(false);
                  }
                }}
                isAdmin={isAdmin}
                adminRole={adminRole}
                showNotify={showNotify}
                allowAnonymousSignIn={allowAnonymousSignIn ?? true}
              />
              <ScrollToTop />
            </PageShell>
          }
        />
        <Route
          path="/partners"
          element={
            <PageShell>
              <Header
                user={user}
                userProfile={userProfile}
                onProfileClick={handleProfileClick}
                branding={branding || undefined}
                contact={contact || undefined}
                isAdmin={!!(user && isAdmin && adminRole)}
                onOpenStrategyCall={() => navigate('/book-a-call')}
                {...sharedHeaderProps}
              />
              <PageMain>
              <CorporatePartnersPage />
              </PageMain>
              <Footer brandName={branding?.brandName} brandTagline={branding?.brandTagline} contact={contact || undefined} {...sharedFooterProps} />
              <ProfileModal
                isOpen={isProfileOpen}
                onClose={() => setIsProfileOpen(false)}
                user={user}
                userProfile={userProfile}
                onSaveProfile={handleSaveProfile}
                onGoogleSignIn={handleGoogleSignIn}
                onEmailSignIn={handleEmailSignIn}
                onEmailRegister={handleEmailRegister}
                onPasswordReset={handlePasswordReset}
                onSignOut={handleSignOut}
                onOpenAdminPanel={() => {
                  if (isAdmin && user && adminRole) {
                    navigate('/admin');
                    setIsProfileOpen(false);
                  }
                }}
                isAdmin={isAdmin}
                adminRole={adminRole}
                showNotify={showNotify}
                allowAnonymousSignIn={allowAnonymousSignIn ?? true}
              />
              <ScrollToTop />
            </PageShell>
          }
        />
        <Route
          path="/warriors/:slug"
          element={
            <PageShell>
              <Header
                user={user}
                userProfile={userProfile}
                onProfileClick={handleProfileClick}
                branding={branding || undefined}
                contact={contact || undefined}
                isAdmin={!!(user && isAdmin && adminRole)}
                onOpenStrategyCall={() => navigate('/book-a-call')}
                {...sharedHeaderProps}
              />
              <PageMain>
              <CMSByNestedPath segment="warriors" brandName={branding?.brandName} branding={branding} contact={contact || undefined} onOpenStrategyCall={() => navigate('/book-a-call')} />
              </PageMain>
              <Footer brandName={branding?.brandName} brandTagline={branding?.brandTagline} contact={contact || undefined} {...sharedFooterProps} />
              <ProfileModal
                isOpen={isProfileOpen}
                onClose={() => setIsProfileOpen(false)}
                user={user}
                userProfile={userProfile}
                onSaveProfile={handleSaveProfile}
                onGoogleSignIn={handleGoogleSignIn}
                onEmailSignIn={handleEmailSignIn}
                onEmailRegister={handleEmailRegister}
                onPasswordReset={handlePasswordReset}
                onSignOut={handleSignOut}
                onOpenAdminPanel={() => {
                  if (isAdmin && user && adminRole) {
                    navigate('/admin');
                    setIsProfileOpen(false);
                  }
                }}
                isAdmin={isAdmin}
                adminRole={adminRole}
                showNotify={showNotify}
                allowAnonymousSignIn={allowAnonymousSignIn ?? true}
              />
              <ScrollToTop />
            </PageShell>
          }
        />
        <Route
          path="/programs/:slug"
          element={
            <PageShell>
              <Header
                user={user}
                userProfile={userProfile}
                onProfileClick={handleProfileClick}
                branding={branding || undefined}
                contact={contact || undefined}
                isAdmin={!!(user && isAdmin && adminRole)}
                onOpenStrategyCall={() => navigate('/book-a-call')}
                {...sharedHeaderProps}
              />
              <PageMain>
              <CMSByNestedPath segment="programs" brandName={branding?.brandName} branding={branding} contact={contact || undefined} onOpenStrategyCall={() => navigate('/book-a-call')} />
              </PageMain>
              <Footer brandName={branding?.brandName} brandTagline={branding?.brandTagline} contact={contact || undefined} {...sharedFooterProps} />
              <ProfileModal
                isOpen={isProfileOpen}
                onClose={() => setIsProfileOpen(false)}
                user={user}
                userProfile={userProfile}
                onSaveProfile={handleSaveProfile}
                onGoogleSignIn={handleGoogleSignIn}
                onEmailSignIn={handleEmailSignIn}
                onEmailRegister={handleEmailRegister}
                onPasswordReset={handlePasswordReset}
                onSignOut={handleSignOut}
                onOpenAdminPanel={() => {
                  if (isAdmin && user && adminRole) {
                    navigate('/admin');
                    setIsProfileOpen(false);
                  }
                }}
                isAdmin={isAdmin}
                adminRole={adminRole}
                showNotify={showNotify}
                allowAnonymousSignIn={allowAnonymousSignIn ?? true}
              />
              <ScrollToTop />
            </PageShell>
          }
        />
        <Route
          path="/book-a-call"
          element={
            <PageShell>
              <Header
                user={user}
                userProfile={userProfile}
                onProfileClick={handleProfileClick}
                branding={branding || undefined}
                contact={contact || undefined}
                isAdmin={!!(user && isAdmin && adminRole)}
                onOpenStrategyCall={() => navigate('/book-a-call')}
                {...sharedHeaderProps}
              />
              <PageMain>
              <BookStrategyCallPage />
              </PageMain>
              <Footer brandName={branding?.brandName} brandTagline={branding?.brandTagline} contact={contact || undefined} {...sharedFooterProps} />
              <ProfileModal
                isOpen={isProfileOpen}
                onClose={() => setIsProfileOpen(false)}
                user={user}
                userProfile={userProfile}
                onSaveProfile={handleSaveProfile}
                onGoogleSignIn={handleGoogleSignIn}
                onEmailSignIn={handleEmailSignIn}
                onEmailRegister={handleEmailRegister}
                onPasswordReset={handlePasswordReset}
                onSignOut={handleSignOut}
                onOpenAdminPanel={() => {
                  if (isAdmin && user && adminRole) {
                    navigate('/admin');
                    setIsProfileOpen(false);
                  }
                }}
                isAdmin={isAdmin}
                adminRole={adminRole}
                showNotify={showNotify}
                allowAnonymousSignIn={allowAnonymousSignIn ?? true}
              />
              <ScrollToTop />
            </PageShell>
          }
        />
        <Route
          path="/:slug"
          element={
            <PageShell>
              <Header
                user={user}
                userProfile={userProfile}
                onProfileClick={handleProfileClick}
                branding={branding || undefined}
                contact={contact || undefined}
                isAdmin={!!(user && isAdmin && adminRole)}
                onOpenStrategyCall={() => navigate('/book-a-call')}
                {...sharedHeaderProps}
              />
              <PageMain>
              <CMSBySlugOrNotFound brandName={branding?.brandName} branding={branding} contact={contact || undefined} onOpenStrategyCall={() => navigate('/book-a-call')} />
              </PageMain>
              <Footer brandName={branding?.brandName} brandTagline={branding?.brandTagline} contact={contact || undefined} {...sharedFooterProps} />
              <ProfileModal
                isOpen={isProfileOpen}
                onClose={() => setIsProfileOpen(false)}
                user={user}
                userProfile={userProfile}
                onSaveProfile={handleSaveProfile}
                onGoogleSignIn={handleGoogleSignIn}
                onEmailSignIn={handleEmailSignIn}
                onEmailRegister={handleEmailRegister}
                onPasswordReset={handlePasswordReset}
                onSignOut={handleSignOut}
                onOpenAdminPanel={() => {
                  if (isAdmin && user && adminRole) {
                    navigate('/admin');
                    setIsProfileOpen(false);
                  }
                }}
                isAdmin={isAdmin}
                adminRole={adminRole}
                showNotify={showNotify}
                allowAnonymousSignIn={allowAnonymousSignIn ?? true}
              />
              <ScrollToTop />
            </PageShell>
          }
        />
        <Route
          path="/"
          element={
            <PageShell>
              <Header
                user={user}
                userProfile={userProfile}
                onProfileClick={handleProfileClick}
                branding={branding || undefined}
                contact={contact || undefined}
                isAdmin={!!(user && isAdmin && adminRole)}
                onOpenStrategyCall={() => navigate('/book-a-call')}
                {...sharedHeaderProps}
              />
              <PageMain>
              <CMSPageView path="/" brandName={branding?.brandName} branding={branding} contact={contact || undefined} onOpenStrategyCall={() => navigate('/book-a-call')} />
              </PageMain>
              <Footer brandName={branding?.brandName} brandTagline={branding?.brandTagline} contact={contact || undefined} {...sharedFooterProps} />
              <ProfileModal
                isOpen={isProfileOpen}
                onClose={() => setIsProfileOpen(false)}
                user={user}
                userProfile={userProfile}
                onSaveProfile={handleSaveProfile}
                onGoogleSignIn={handleGoogleSignIn}
                onEmailSignIn={handleEmailSignIn}
                onEmailRegister={handleEmailRegister}
                onPasswordReset={handlePasswordReset}
                onSignOut={handleSignOut}
                onOpenAdminPanel={() => {
                  if (isAdmin && user && adminRole) {
                    navigate('/admin');
                    setIsProfileOpen(false);
                  }
                }}
                isAdmin={isAdmin}
                adminRole={adminRole}
                showNotify={showNotify}
                allowAnonymousSignIn={allowAnonymousSignIn ?? true}
              />
              <ScrollToTop />
            </PageShell>
          }
        />
        <Route
          path="*"
          element={
            <PageShell>
              <Header
                user={user}
                userProfile={userProfile}
                onProfileClick={handleProfileClick}
                branding={branding || undefined}
                contact={contact || undefined}
                isAdmin={!!(user && isAdmin && adminRole)}
                onOpenStrategyCall={() => navigate('/book-a-call')}
                {...sharedHeaderProps}
              />
              <PageMain>
              <NotFoundPage />
              </PageMain>
              <Footer brandName={branding?.brandName} brandTagline={branding?.brandTagline} contact={contact || undefined} {...sharedFooterProps} />
              <ProfileModal
                isOpen={isProfileOpen}
                onClose={() => setIsProfileOpen(false)}
                user={user}
                userProfile={userProfile}
                onSaveProfile={handleSaveProfile}
                onGoogleSignIn={handleGoogleSignIn}
                onEmailSignIn={handleEmailSignIn}
                onEmailRegister={handleEmailRegister}
                onPasswordReset={handlePasswordReset}
                onSignOut={handleSignOut}
                onOpenAdminPanel={() => {
                  if (isAdmin && user && adminRole) {
                    navigate('/admin');
                    setIsProfileOpen(false);
                  }
                }}
                isAdmin={isAdmin}
                adminRole={adminRole}
                showNotify={showNotify}
                allowAnonymousSignIn={allowAnonymousSignIn ?? true}
              />
            </PageShell>
          }
        />
      </Routes>
      <TermsOfService isOpen={showTerms} onClose={() => setShowTerms(false)} />
      <PrivacyPolicy isOpen={showPrivacy} onClose={() => setShowPrivacy(false)} />
    </CookieConsentProvider>
  );
}

export default App;
